import { useMemo, useState } from "react";
import { Plus, Minus } from "lucide-react";
import Lightbox from "@/components/Lightbox";

const INITIAL_COUNT = 24;

function RangeTile({ m }) {
  const [open, setOpen] = useState(false);
  if (m.image) {
    return (
      <div className="group" data-testid={`range-tile-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <div
          className="overflow-hidden aspect-[4/3] bg-secondary/40 cursor-zoom-in"
          onClick={() => setOpen(true)}
        >
          <img
            src={m.image}
            alt={m.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
        </div>
        <p className="mt-2.5 text-sm font-light tracking-tight">{m.name}</p>
        <p className="text-[10px] tracking-[0.18em] uppercase text-secondary-foreground">{m.series}</p>
        <Lightbox
          image={open ? { src: m.image, name: m.name, series: m.series } : null}
          onClose={() => setOpen(false)}
        />
      </div>
    );
  }
  return (
    <div
      data-testid={`range-tile-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="border border-border aspect-[4/3] flex flex-col justify-center items-center text-center p-3 transition-colors duration-300 hover:bg-secondary/40"
    >
      <p className="font-serif text-base font-light tracking-tight leading-tight">{m.name}</p>
      <p className="mt-1 text-[10px] tracking-[0.18em] uppercase text-secondary-foreground">{m.series}</p>
    </div>
  );
}

export default function QuartzRange({ range, brandKey }) {
  const [series, setSeries] = useState("All");
  const [expanded, setExpanded] = useState(false);

  const seriesList = useMemo(
    () => ["All", ...Array.from(new Set(range.map((m) => m.series)))],
    [range]
  );
  const filtered = useMemo(
    () => (series === "All" ? range : range.filter((m) => m.series === series)),
    [range, series]
  );
  const visible = expanded || series !== "All" ? filtered : filtered.slice(0, INITIAL_COUNT);

  return (
    <div className="mt-16" data-testid={`range-${brandKey}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary-foreground">
          The full range — {range.length} models
        </p>
        <div className="flex flex-wrap gap-2">
          {seriesList.map((s) => (
            <button
              key={s}
              data-testid={`range-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => {
                setSeries(s);
                setExpanded(false);
              }}
              className={`text-[10px] tracking-[0.18em] uppercase px-4 py-2 border transition-colors duration-300 ${
                series === s
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-secondary-foreground hover:border-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
        {visible.map((m) => (
          <RangeTile key={m.name} m={m} />
        ))}
      </div>

      {series === "All" && filtered.length > INITIAL_COUNT && (
        <button
          data-testid={`range-toggle-${brandKey}`}
          onClick={() => setExpanded(!expanded)}
          className="mt-10 mx-auto flex items-center gap-2 text-xs tracking-[0.2em] uppercase border border-border px-6 py-3 transition-colors duration-300 hover:bg-foreground hover:text-background"
        >
          {expanded ? (
            <>
              Show less <Minus className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              View all {filtered.length} models <Plus className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
